import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { SeoHead } from '../components/SeoHead';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Calendar, MapPin, Users, Video, Loader2, Search, X, 
  ChevronRight, Trophy, Radio, Clock, Flame, Play, Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hero background image
const HERO_IMAGE = 'https://images.unsplash.com/photo-1677617586882-2b494292ebbe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920';

// Helper to parse date without timezone shift
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  // If date-only string (e.g., "2026-04-11"), parse as local date to avoid UTC shift
  const str = String(dateStr);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  // ISO string with time — parse in UTC then format in UTC
  const dt = new Date(str);
  return new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
};

// Helper to format date range
const formatDateRange = (startDate, endDate) => {
  if (!startDate) return 'TBD';
  const start = parseDate(startDate);
  const end = endDate ? parseDate(endDate) : null;
  
  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const startDay = start.getDate();
  
  if (!end || start.toDateString() === end.toDateString()) {
    return `${startMonth} ${startDay}`;
  }
  
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
  const endDay = end.getDate();
  
  if (startMonth === endMonth) {
    return `${startMonth} ${startDay}-${endDay}`;
  }
  
  return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
};

// Filter Pill Component
const FilterPill = ({ active, onClick, children, count }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap flex items-center gap-2 ${
      active 
        ? 'bg-white text-black' 
        : 'bg-transparent border border-white/20 text-white hover:bg-white/10'
    }`}
  >
    {children}
    {count !== undefined && count > 0 && (
      <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? 'bg-black/20' : 'bg-white/20'}`}>
        {count}
      </span>
    )}
  </button>
);

// Event Card - Cinematic style
const EventCard = ({ event, games, onNavigate }) => {
  const eventGames = games.filter(g => g.event_id === event.id || g.tournament_id === event.id);
  const liveGames = eventGames.filter(g => g.status === 'live' || g.status === 'halftime');
  const dateRange = formatDateRange(event.date, event.end_date);
  
  const sportColors = {
    basketball: { accent: '#FF453A', glow: 'shadow-orange-500/20' },
    soccer: { accent: '#30D158', glow: 'shadow-green-500/20' },
    volleyball: { accent: '#0A84FF', glow: 'shadow-blue-500/20' },
    football: { accent: '#AF52DE', glow: 'shadow-purple-500/20' },
    default: { accent: '#FF453A', glow: 'shadow-red-500/20' }
  };
  const colors = sportColors[event.sport?.toLowerCase()] || sportColors.default;

  return (
    <div 
      onClick={onNavigate}
      className={`group relative bg-[#141414] border border-white/10 rounded-2xl overflow-hidden cursor-pointer hover:border-white/20 transition-all duration-300 hover:shadow-xl ${colors.glow}`}
      data-testid={`event-card-${event.id}`}
    >
      {/* Live indicator */}
      {liveGames.length > 0 && (
        <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 bg-black/60 backdrop-blur-md px-2.5 py-1 rounded-full">
          <span className="w-2 h-2 bg-[#E5FF00] rounded-full animate-pulse" />
          <span className="text-[#E5FF00] text-xs font-bold">{liveGames.length} LIVE</span>
        </div>
      )}

      {/* Content */}
      <div className="p-5">
        {/* Logo + Sport Tag Row */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-3">
            <img src={event.logo_url || (event.id === 'efb99264-287c-4957-857e-59fac86cda01' ? '/ek-cali-classic-logo.png' : '/rooam-logo.png')} alt="" className="w-12 h-12 object-contain shrink-0" />
            <div 
              className="inline-block px-2.5 py-1 rounded text-xs font-bold uppercase tracking-wider truncate max-w-[60%]"
              style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}
            >
              {event.sport || 'Sports'}
            </div>
          </div>
          {event.is_featured && (
            <div className="flex items-center gap-1.5 bg-[#E5FF00] text-black px-2.5 py-1 rounded-full text-xs font-bold shrink-0">
              <Star className="w-3 h-3" fill="currentColor" />
              FEATURED
            </div>
          )}
        </div>
        
        {/* Event Name */}
        <h3 
          className="text-xl font-bold text-white uppercase tracking-tight mb-2 line-clamp-2 group-hover:text-[#FF453A] transition-colors"
          style={{ fontFamily: 'Oswald, sans-serif' }}
        >
          {event.name}
        </h3>
        
        {/* Meta Row */}
        <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-400 mb-4">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4 text-[#FF453A]" />
            <span>{dateRange}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <MapPin className="w-4 h-4 text-[#FF453A]" />
            <span className="truncate max-w-[150px]">{event.location || 'TBD'}</span>
          </div>
        </div>
        
        {/* Stats Row */}
        <div className="flex items-center justify-between pt-4 border-t border-white/10">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-sm text-zinc-500">
              <Users className="w-4 h-4" />
              <span>{event.teams || eventGames.length || 0} Teams</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-zinc-500">
              <Video className="w-4 h-4" />
              <span>{event.games || eventGames.length} Games</span>
            </div>
          </div>
          
          {/* Arrow */}
          <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-[#FF453A] transition-colors">
            <ChevronRight className="w-4 h-4 text-zinc-400 group-hover:text-white" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Game Card - Compact
const GameCard = ({ game }) => {
  const isLive = game.status === 'live' || game.status === 'halftime';
  const isFinal = game.status === 'final' || game.status === 'completed' || game.status === 'ended';
  
  return (
    <Link 
      to={`/game-live/${game.game_id || game.id}`}
      className="group block bg-[#141414] border border-white/10 rounded-xl overflow-hidden hover:border-[#FF453A]/50 transition-all"
      data-testid={`game-card-${game.game_id || game.id}`}
    >
      <div className="p-4">
        {/* Status Badge */}
        <div className="flex items-center justify-between mb-3">
          {isLive ? (
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 bg-[#E5FF00] rounded-full animate-pulse" />
              <span className="text-xs font-bold text-[#E5FF00] uppercase">
                {game.status === 'halftime' ? 'Halftime' : 'Live'}
              </span>
            </div>
          ) : isFinal ? (
            <span className="text-xs font-bold text-white/40 uppercase">Final</span>
          ) : (
            <span className="text-xs text-zinc-500">
              {game.scheduled_time ? new Date(game.scheduled_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Upcoming'}
            </span>
          )}
          
          {(game.playback_url || game.video_url) && (
            <Badge className="bg-[#FF453A]/20 text-[#FF453A] border-0 text-[10px]">
              <Play className="w-3 h-3 mr-1" fill="currentColor" />
              Video
            </Badge>
          )}
        </div>
        
        {/* Teams */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-white truncate pr-2">
              {game.home_team_name || game.team1?.name || 'Home'}
            </span>
            <span 
              className={`text-lg font-bold tabular-nums ${isLive ? 'text-[#FF453A]' : 'text-white'}`}
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              {game.score?.home ?? 0}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-semibold text-white truncate pr-2">
              {game.away_team_name || game.team2?.name || 'Away'}
            </span>
            <span 
              className={`text-lg font-bold tabular-nums ${isLive ? 'text-[#FF453A]' : 'text-white'}`}
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              {game.score?.away ?? 0}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

const Events = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  
  const activeTab = searchParams.get('active_tab') || 'upcoming';

  // Filters
  const [sportFilter, setSportFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  const setActiveTab = (tab) => {
    setSearchParams({ active_tab: tab });
  };

  const fetchData = useCallback(async (isRetry = false) => {
    if (!isRetry) setLoading(true);
    setError(null);
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      
      const [eventsRes, gamesRes] = await Promise.all([
        fetch(`${API_URL}/api/events`, { signal: controller.signal }),
        fetch(`${API_URL}/api/browse/games`, { signal: controller.signal })
      ]);
      
      clearTimeout(timeoutId);
      
      if (eventsRes.ok) {
        const data = await eventsRes.json();
        setEvents(data);
      }
      
      if (gamesRes.ok) {
        const gamesData = await gamesRes.json();
        setGames(gamesData);
      }
      
      if (!eventsRes.ok && !gamesRes.ok) {
        setError('Unable to load data. Please try again.');
      }
      
      setRetryCount(0);
    } catch (err) {
      console.error('Error fetching data:', err);
      if (retryCount < 3 && (err.name === 'AbortError' || err.name === 'TypeError')) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchData(true), 2000);
        return;
      }
      setError('Connection error. Please check your internet and try again.');
    }
    setLoading(false);
  }, [retryCount]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRetry = () => {
    setRetryCount(0);
    fetchData();
  };

  // Get unique filter options - normalize case and split multi-sport entries
  const filterOptions = useMemo(() => {
    const sportsSet = new Set();
    events.forEach(e => {
      if (e.sport) {
        // Split by comma to handle multi-sport events like "Soccer, Basketball, Flag Football"
        const sportsList = e.sport.split(',').map(s => s.trim());
        sportsList.forEach(sport => {
          if (sport) {
            // Normalize to Title Case
            const normalized = sport.charAt(0).toUpperCase() + sport.slice(1).toLowerCase();
            sportsSet.add(normalized);
          }
        });
      }
    });
    const sports = [...sportsSet].sort();
    const locations = [...new Set(events.map(e => e.location).filter(Boolean))].sort();
    return { sports, locations };
  }, [events]);

  // Categorize events
  const categorizedEvents = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.setHours(0, 0, 0, 0));
    const todayEnd = new Date(now.setHours(23, 59, 59, 999));
    
    // Apply filters (no longer filtering out "test" events)
    const filtered = events.filter(e => {
      if (searchTerm) {
        const query = searchTerm.toLowerCase();
        const matchesName = e.name?.toLowerCase().includes(query);
        const matchesLocation = e.location?.toLowerCase().includes(query);
        const matchesSport = e.sport?.toLowerCase().includes(query);
        if (!matchesName && !matchesLocation && !matchesSport) return false;
      }
      
      // Sport filter - check if any of the event's sports match the filter
      if (sportFilter) {
        const eventSports = (e.sport || '').split(',').map(s => s.trim().toLowerCase());
        if (!eventSports.some(s => s === sportFilter.toLowerCase())) {
          return false;
        }
      }
      
      if (locationFilter && e.location !== locationFilter) return false;
      return true;
    });

    const upcoming = [];
    const today = [];
    const past = [];
    const featured = [];

    filtered.forEach(event => {
      if (event.is_featured) featured.push(event);
      
      const eventDate = event.date ? parseDate(event.date) : null;
      const endDate = event.end_date ? parseDate(event.end_date) : eventDate;
      
      // Check if any games are live
      const eventGames = games.filter(g => g.event_id === event.id);
      const hasLiveGames = eventGames.some(g => g.status === 'live' || g.status === 'halftime');
      
      if (hasLiveGames) {
        today.push(event);
      } else if (!eventDate) {
        upcoming.push(event);
      } else if (eventDate > todayEnd) {
        upcoming.push(event);
      } else if (endDate && endDate >= todayStart && eventDate <= todayEnd) {
        today.push(event);
      } else if (eventDate < todayStart) {
        past.push(event);
      } else {
        upcoming.push(event);
      }
    });

    // Sort: upcoming by date ascending, past by date descending
    upcoming.sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
    past.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    
    return { upcoming, today, past, featured };
  }, [events, games, searchTerm, sportFilter, locationFilter]);

  // Single games (not attached to events)
  const singleGames = useMemo(() => {
    const eventIds = events.map(e => e.id);
    const isPlaceholderName = (name) => {
      if (!name) return true;
      const t = String(name).trim();
      if (!t || t.length <= 2) return true;
      if (/^\d+$/.test(t)) return true;
      return ['home', 'away', 'team 1', 'team 2', 'test'].includes(t.toLowerCase());
    };
    return games.filter(g => {
      if (eventIds.includes(g.event_id) || eventIds.includes(g.tournament_id)) return false;
      if (g.tournament_name) return false;
      if (g.hidden) return false;
      // Skip games with placeholder names (test/ghost games)
      if (isPlaceholderName(g.home_team_name) && isPlaceholderName(g.away_team_name)) return false;
      if (isPlaceholderName(g.team1?.name) && isPlaceholderName(g.team2?.name)) return false;
      // Keep games with video or live games
      const hasVideo = g.playback_url || g.video_url;
      const isLive = g.status === 'live' || g.status === 'halftime';
      return hasVideo || isLive;
    }).slice(0, 8);
  }, [events, games]);

  const currentEvents = activeTab === 'upcoming' 
    ? categorizedEvents.upcoming 
    : activeTab === 'today' 
      ? categorizedEvents.today 
      : categorizedEvents.past;

  const tabConfig = [
    { id: 'upcoming', label: 'Upcoming', icon: Calendar, count: categorizedEvents.upcoming.length },
    { id: 'today', label: 'Live Now', icon: Flame, count: categorizedEvents.today.length },
    { id: 'past', label: 'Replays', icon: Clock, count: categorizedEvents.past.length },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 text-[#FF453A] animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Loading events...</p>
          {retryCount > 0 && (
            <p className="text-zinc-500 text-sm mt-2">Retrying... ({retryCount}/3)</p>
          )}
        </div>
      </div>
    );
  }

  if (error && events.length === 0) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Unable to Load</h2>
          <p className="text-zinc-400 mb-6">{error}</p>
          <Button onClick={handleRetry} className="bg-[#FF453A] hover:bg-[#FF5A50] text-white font-semibold">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <SeoHead
        title="Live & Upcoming Sports Events"
        description="Browse live, upcoming, and replay sports events on Rooam Sports Network. AAU basketball tournaments, showcases, league games, and more — streamed live with real-time stats."
        path="/events"
      />
      {/* Cinematic Hero */}
      <div className="relative h-[40vh] min-h-[300px] w-full overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE}
            alt="Basketball Court"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/30" />
        </div>
        
        {/* Hero Content */}
        <div className="absolute bottom-0 left-0 right-0 px-4 md:px-8 pb-6">
          <div className="max-w-7xl mx-auto">
            <h1 
              className="text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tighter text-white leading-none"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              Events & Games
            </h1>
            <p className="text-zinc-400 mt-2 text-lg">
              Discover live games, upcoming tournaments, and replays
            </p>
          </div>
        </div>
      </div>

      {/* Sticky Command Bar */}
      <div className="sticky top-0 z-40 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Tabs */}
            <div className="flex items-center gap-4 md:gap-6 overflow-x-auto pb-1">
              {tabConfig.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`tab-${tab.id}`}
                    className={`flex items-center gap-2 text-lg md:text-xl font-bold uppercase tracking-tight transition-colors whitespace-nowrap ${
                      isActive ? 'text-white' : 'text-zinc-600 hover:text-zinc-400'
                    }`}
                    style={{ fontFamily: 'Oswald, sans-serif' }}
                  >
                    {tab.id === 'today' && tab.count > 0 && (
                      <span className="w-2 h-2 bg-[#E5FF00] rounded-full animate-pulse" />
                    )}
                    {tab.label}
                    <span className={`text-sm px-2 py-0.5 rounded-full ${isActive ? 'bg-white/20' : 'bg-zinc-800'}`}>
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
            
            {/* Search Toggle */}
            <button 
              onClick={() => setShowSearch(!showSearch)}
              className="p-2 rounded-full hover:bg-white/10 transition-colors shrink-0"
              data-testid="search-toggle"
            >
              {showSearch ? <X className="w-5 h-5" /> : <Search className="w-5 h-5" />}
            </button>
          </div>
          
          {/* Expandable Search */}
          {showSearch && (
            <div className="mt-4">
              <Input
                type="text"
                placeholder="Search events, locations, sports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-input"
                className="w-full bg-white/5 border border-white/10 rounded-full px-5 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#FF453A]/50"
                autoFocus
              />
            </div>
          )}
          
          {/* Filter Pills */}
          {(filterOptions.sports.length > 0 || filterOptions.locations.length > 0) && (
            <div className="flex items-center gap-3 mt-4 overflow-x-auto pb-2 scrollbar-hide">
              <FilterPill 
                active={!sportFilter && !locationFilter} 
                onClick={() => { setSportFilter(''); setLocationFilter(''); }}
              >
                All
              </FilterPill>
              
              {filterOptions.sports.map(sport => (
                <FilterPill 
                  key={sport}
                  active={sportFilter === sport}
                  onClick={() => setSportFilter(sportFilter === sport ? '' : sport)}
                >
                  {sport}
                </FilterPill>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Featured Events */}
        {activeTab === 'upcoming' && categorizedEvents.featured.length > 0 && (
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <Star className="w-5 h-5 text-[#E5FF00]" fill="currentColor" />
              <h2 
                className="text-xs font-bold text-zinc-400 uppercase tracking-[0.2em]"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Featured Events
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categorizedEvents.featured.slice(0, 3).map(event => (
                <EventCard 
                  key={event.id}
                  event={event}
                  games={games}
                  onNavigate={() => navigate(event.is_multi_court ? `/event/${event.id}/courts` : `/event/${event.id}`)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Live Games - Show on Today tab */}
        {activeTab === 'today' && singleGames.filter(g => g.status === 'live').length > 0 && (
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-2 h-2 bg-[#E5FF00] rounded-full animate-pulse" />
              <h2 
                className="text-xs font-bold text-zinc-400 uppercase tracking-[0.2em]"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Live Now
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {singleGames.filter(g => g.status === 'live').map(game => (
                <GameCard key={game.game_id || game.id} game={game} />
              ))}
            </div>
          </section>
        )}

        {/* Section Title */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h2 
              className="text-xs font-bold text-zinc-400 uppercase tracking-[0.2em]"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {activeTab === 'upcoming' ? 'Upcoming Events' : activeTab === 'today' ? "Today's Events" : 'Past Events'}
            </h2>
            <span className="text-xs text-zinc-600">
              {currentEvents.length} event{currentEvents.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Events Grid */}
        {currentEvents.length === 0 ? (
          <div className="text-center py-20 bg-[#141414] rounded-2xl border border-white/10">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-zinc-800/50 flex items-center justify-center">
              <Trophy className="h-10 w-10 text-zinc-600" />
            </div>
            <h3 
              className="text-xl font-bold text-white mb-2 uppercase"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              No Events Found
            </h3>
            <p className="text-zinc-500 mb-6 max-w-md mx-auto">
              {searchTerm 
                ? `No events found for "${searchTerm}". Try a different search.`
                : activeTab === 'upcoming'
                  ? 'No upcoming events scheduled yet.'
                  : activeTab === 'today'
                    ? 'No events happening right now.'
                    : 'No past events to show.'
              }
            </p>
            {(searchTerm || sportFilter || locationFilter) && (
              <Button 
                variant="outline" 
                onClick={() => { setSearchTerm(''); setSportFilter(''); setLocationFilter(''); }}
                className="border-[#FF453A] text-[#FF453A] hover:bg-[#FF453A]/10"
              >
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentEvents.map(event => (
              <EventCard 
                key={event.id}
                event={event}
                games={games}
                onNavigate={() => navigate(event.is_multi_court ? `/event/${event.id}/courts` : `/event/${event.id}`)}
              />
            ))}
          </div>
        )}

        {/* Past Events - Always visible on Upcoming tab so replays are accessible */}
        {activeTab === 'upcoming' && categorizedEvents.past.length > 0 && (
          <section className="mt-12" data-testid="past-events-section">
            <div className="flex items-center gap-3 mb-6">
              <Play className="w-4 h-4 text-[#FF453A]" />
              <h2 
                className="text-xs font-bold text-zinc-400 uppercase tracking-[0.2em]"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Past Events
              </h2>
              <span className="text-xs text-zinc-600">
                {categorizedEvents.past.length} event{categorizedEvents.past.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categorizedEvents.past.map(event => (
                <EventCard 
                  key={event.id}
                  event={event}
                  games={games}
                  onNavigate={() => navigate(event.is_multi_court ? `/event/${event.id}/courts` : `/event/${event.id}`)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Recent Games - at the very bottom */}
        {singleGames.length > 0 && activeTab !== 'today' && (
          <section className="mt-12" data-testid="recent-games-section">
            <h2 
              className="text-xs font-bold text-zinc-400 uppercase tracking-[0.2em] mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Recent Games
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {singleGames.map(game => (
                <GameCard key={game.game_id || game.id} game={game} />
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <div className="mt-12 relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#FF453A] to-[#FF6B5A]">
          <div className="relative p-8 md:p-12 text-center">
            <h3 
              className="text-2xl md:text-3xl font-black text-white mb-3 uppercase tracking-tight"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              Ready to Go Live?
            </h3>
            <p className="text-white/80 mb-6 max-w-lg mx-auto">
              Stream your tournament on Rooam Sports Network and reach fans everywhere
            </p>
            <Button asChild size="lg" className="bg-white text-black hover:bg-zinc-100 font-bold shadow-xl rounded-full">
              <Link to="/partner">
                <Video className="h-5 w-5 mr-2" />
                Create Tournament
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Events;
